import { actionsFromList } from '@mgc/map-client-shared/lib/ReduxTools';
import selectors from '../selectors';
import moment from 'moment';
import UserActions from './user';
import utils from '../utils';

function _assembleShoppingCart(userId, state) {
    const offer = selectors.offer.getSelectedOffer(state);
    const serversBySite = selectors.offer.getServersGroupedBySite(state);
    const soldTo = selectors.cart.getSoldTo(state);
    const timezoneOffset = moment().utcOffset() / 60;
    return selectors.cart.getCartContents(state).filter(item => item.itemDataChanged === true && item.addedToCart)
        .map((item) => {
            const offerEnd = moment(offer.validTo);
            let validTo = moment(item.startDate).add(item.monthCount, 'months');
            if (offer.enterprise_deal || utils.isDateRangeSoldTO(soldTo) || offer.dailyPricingEnabled) {
                validTo = moment(item.endDate);
            } else {
                if (offer.weeklyPricingEnabled && item.weekCount) {
                    validTo = validTo.add(item.weekCount, 'weeks');
                }
            }
            validTo = validTo.isAfter(offerEnd) ? offerEnd : validTo;

            return {
                id: item.cartId,
                amount: item.subtotal,
                duration: item.monthCount,
                offer: selectors.offer.getSelectedOfferId(state),
                product: item.pricing.product.id || null,
                save_for_later: item.saveForLater,
                seats: item.seats,
                server: serversBySite[item.siteIndex].servers[item.serverIndex].id || null,
                site: serversBySite[item.siteIndex].id || null,
                tiered_discount: item.isTieredDiscountApplied || false,
                timezoneOffset,
                transaction: '',
                user: userId || null,
                validFrom: moment(item.startDate).toISOString(),
                validTo: validTo.toISOString(),
                weeks: item.weekCount || 0,
                wish_list_name: '',
            };
        });
}

export default class CartActions {
    static types = actionsFromList([
        'ADD_ITEM_TO_CART',
        'SELECT_ITEM_IN_CART',
        'REMOVE_ITEM_FROM_CART',
        'UPDATE_ITEM_SEATS',
        'UPDATE_ITEM_MONTH_COUNT',
        'UPDATE_ITEM_WEEK_COUNT',
        'UPDATE_ITEM_START_DATE',
        'UPDATE_ITEM_DATE_RANGE',
        'UPDATE_ITEM_SERVER',
        'UPDATE_CART_INSTRUCTIONS',
        'CLEAR_CART',
        'UPDATE_ITEM_END_DATE',
        'UPDATE_SAVE_FOR_LATER_LIST',
        'ADD_INITIAL_ITEMS_TO_CART',
        'ADD_UNSAVED_ITEMS_TO_CART',
    ]);

    // sync --------------------------------------------------------------------

    static selectItemInCart = (itemIndex) => ({
        type: CartActions.types.SELECT_ITEM_IN_CART,
        payload: { itemIndex },
    });

    static updateCartItems = (unsavedItems) => ({
        type: CartActions.types.ADD_UNSAVED_ITEMS_TO_CART,
        payload: { unsavedItems },
    });

    static setItems = () => (dispatch, getState) => {
        // Access the Redux state using getState
        const state = getState();

        // Get the selected offer using the selector
        const selectedOffer = selectors.offer.getSelectedOffer(state);
        const user = selectors.user.getMyself(state);
        const userCart = user && user.cart ? user.cart : [];
        const serversBySite = selectors.offer.getServersGroupedBySite(state);
        const unsaved_cart_items = selectors.cart.getCartContents(state).filter((item) => !item.addedToCart);
        dispatch({
            type: CartActions.types.ADD_INITIAL_ITEMS_TO_CART,
            payload: { userCart, selectedOffer, serversBySite }
        });
        // retaining primary screen items not added to cart after updating data from DB.
        if (unsaved_cart_items && unsaved_cart_items.length > 0)
            dispatch(CartActions.updateCartItems(unsaved_cart_items));
    };

    static removeItemFromCart = (itemIndex, cartId) => (dispatch, getState) => {
        const state = getState(); // Get the current state
        let triggerAPI = false;
        let cart_id_data = cartId;
        if (Array.isArray(cartId) && cartId.length > 0) {
            triggerAPI = true;
            cart_id_data = cartId;
        } else if (cartId && cartId.length > 0) {
            cart_id_data = [cartId];
            triggerAPI = true;
        } else {
            // If cartId is empty or null, simply remove the item locally
            console.log('No cart data available, skipping API call.');
            // Dispatch the action to remove the item from the cart
            dispatch({
                type: CartActions.types.REMOVE_ITEM_FROM_CART,
                payload: { itemIndex, cartId }
            });

            return Promise.resolve(); // Return resolved promise to avoid breaking flow
        }
        if (triggerAPI) {
            return state.api
                .deleteShoppingCartBulk(cart_id_data) // Make the API call
                .then((result) => {
                    if (result && result.data) {
                        dispatch(UserActions.receivedUser(result));
                    }
                    dispatch(CartActions.setItems());
                })
                .catch((error) => {
                    console.error('Error removing item from cart:', error);
                });
        }
    };


    static updateItemSeats = (cartIndex, seats) => ({
        type: CartActions.types.UPDATE_ITEM_SEATS,
        payload: { cartIndex, seats },
    });

    static updateItemMonthCount = (cartIndex, monthCount) => ({
        type: CartActions.types.UPDATE_ITEM_MONTH_COUNT,
        payload: { cartIndex, monthCount },
    });

    static updateItemWeekCount = (cartIndex, weekCount) => ({
        type: CartActions.types.UPDATE_ITEM_WEEK_COUNT,
        payload: { cartIndex, weekCount },
    });

    static updateItemStartDate = (cartIndex, startDate) => ({
        type: CartActions.types.UPDATE_ITEM_START_DATE,
        payload: { cartIndex, startDate },
    });

    static updateItemDateRange = (cartIndex, startDate, endDate) => ({
        type: CartActions.types.UPDATE_ITEM_DATE_RANGE,
        payload: { cartIndex, startDate, endDate },
    });

    static updateItemEndDate = (cartIndex, endDate) => ({
        type: CartActions.types.UPDATE_ITEM_END_DATE,
        payload: { cartIndex, endDate },
    });

    static updateItemServer = (cartIndex, siteIndex, serverIndex) => ({
        type: CartActions.types.UPDATE_ITEM_SERVER,
        payload: { cartIndex, siteIndex, serverIndex },
    });

    static updateCartInstructions = (instructions) => ({
        type: CartActions.types.UPDATE_CART_INSTRUCTIONS,
        payload: { instructions },
    });

    static updateSaveForLaterList = (cartIndex, saveForLater) => ({
        type: CartActions.types.UPDATE_SAVE_FOR_LATER_LIST,
        payload: { cartIndex, saveForLater },
    });

    static clearCart = () => ({ type: CartActions.types.CLEAR_CART });

    static saveShoppingCart = (userId) => (dispatch, getState) => {
        const state = getState();
        const cart_data = _assembleShoppingCart(userId, state);

        if (cart_data && cart_data.length > 0) {
            return state.api
                .saveShoppingCart(cart_data)
                .then((result) => {
                    if (result && result.data) {
                        dispatch(UserActions.receivedUser(result));
                    }
                    dispatch(CartActions.setItems());
                })
                .catch((error) => {
                    console.error('Error saving shopping cart:', error);
                });
        } else {
            console.log('No cart data available, skipping API call.');
            return Promise.resolve(); // Return a resolved promise to handle as a no-op
        }
    };

    static addProductToCart = (productIndex) => (dispatch, getState) => {
        const state = getState();
        const siteIndex = selectors.user.getFavoriteSiteIndex(state);
        const serverIndex = selectors.user.getFavoriteServerIndex(state);
        const hasOnlyWeekly = selectors.offer.getHasOnlyWeeklyPricing(state);
        const amounts = selectors.offer.getSelectedOffer(state).pricing.map(item => item.monthly[0].amount);
        const uniqueAmounts = [...new Set(amounts)];
        const amount = uniqueAmounts.length === 1 ? uniqueAmounts[0] : null;
        const soldTo = selectors.cart.getSoldTo(state);
        const dailyPricingEnabled = selectors.offer.getSelectedOffer(state).dailyPricingEnabled;
        const offerEndDate = selectors.offer.getSelectedOffer(state).validTo;

        dispatch({
            type: CartActions.types.ADD_ITEM_TO_CART,
            payload: {
                productIndex,
                siteIndex,
                serverIndex,
                monthCount: hasOnlyWeekly ? 0 : amount,
                weekCount: hasOnlyWeekly ? 1 : 0,
                dayCount: 0,
                soldTo,
                dailyPricingEnabled,
                offerEndDate
            },
        });
    };
}



// WEBPACK FOOTER //
// ./app/actions/cart.js